import { useParams } from "react-router-dom";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { t } from "i18next";
import { Spacer, Switch, TextCapitalized, triggerToast } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  createEmailSubscriptionAsync,
  createPlatformSubscriptionAsync,
  deleteEmailSubscriptionAsync,
  deletePlatformSubscriptionAsync,
  getEmailSubscriptionsListAsync,
  getPlatformSubscriptionsAsync,
  selectEmailSubscriptionsList,
  selectPlatformSubscriptionsList,
  notificationsSlice,
  selectGetEmailSubscriptionsListStatus,
  selectGetPlatformSubscriptionsStatus,
  selectUpdateCompanySubscriptionsParametersStatus,
} from "./notificationsSlice";
import { selectCompany } from "../homeSlice";
import { notificationsCategories, notificationsList } from "./lib";
import { NotificationListType, NotificationsType } from "./notificationsApi";
import ParametersSelect from "./components/ParametersSelect";

export const ManagementTab = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [parameters, setParameters] = useState<
    Record<string, Record<string, string> | null>
  >({});

  const emailSubscriptionsList = useAppSelector(selectEmailSubscriptionsList);
  const platformSubscriptionsList = useAppSelector(
    selectPlatformSubscriptionsList,
  );
  const getEmailSubscriptionsListStatus = useAppSelector(
    selectGetEmailSubscriptionsListStatus,
  );
  const getPlatformSubscriptionsStatus = useAppSelector(
    selectGetPlatformSubscriptionsStatus,
  );
  const updateCompanySubscriptionsParametersStatus = useAppSelector(
    selectUpdateCompanySubscriptionsParametersStatus,
  );

  useEffect(() => {
    if (updateCompanySubscriptionsParametersStatus === "success") {
      dispatch(
        notificationsSlice.actions.resetUpdateCompanySubscriptionsParametersStatus(),
      );
      triggerToast(
        t("notifications.updateCompanySubscriptionsParameters.success"),
        "valid",
      );
    } else if (updateCompanySubscriptionsParametersStatus === "failed") {
      triggerToast(
        t("notifications.updateCompanySubscriptionsParameters.failed"),
        "error",
      );
      dispatch(
        notificationsSlice.actions.resetUpdateCompanySubscriptionsParametersStatus(),
      );
    }
  }, [updateCompanySubscriptionsParametersStatus, dispatch]);

  const handleDeleteEmailSubscription = useCallback(
    (subscriptionUuid: string) => {
      void dispatch(deleteEmailSubscriptionAsync(subscriptionUuid));
    },
    [dispatch],
  );

  const handleDeletePlatformSubscription = useCallback(
    (subscriptionUuid: string) => {
      void dispatch(deletePlatformSubscriptionAsync(subscriptionUuid));
    },
    [dispatch],
  );

  const handleAddEmailSubscription = useCallback(
    (
      notificationType: NotificationsType,
      role: string,
      notif: NotificationListType,
    ) => {
      if (!company) {
        return;
      }
      void dispatch(
        createEmailSubscriptionAsync({
          company: company.uuid,
          type: notificationType,
          role,
          parameters:
            parameters[String(notificationType)] ?? notif.defaultParameters,
        }),
      );
    },
    [dispatch, company, parameters],
  );

  const handleAddPlatformSubscription = useCallback(
    (notification: NotificationListType) => {
      if (!company) {
        return;
      }
      void dispatch(
        createPlatformSubscriptionAsync({
          company: company.uuid,
          type: notification.type,
          parameters:
            parameters[String(notification.type)] ??
            notification.defaultParameters,
        }),
      );
    },
    [dispatch, company, parameters],
  );

  const allSubscriptionsDisplayed: NotificationListType[] = useMemo(() => {
    if (!emailSubscriptionsList || !platformSubscriptionsList) return [];

    return notificationsList.map((notification) => {
      const ownerEmailSubscription =
        emailSubscriptionsList.find(
          (subscription) =>
            subscription.type === notification.type &&
            subscription.role === "OWNER",
        )?.uuid ?? null;

      const adminEmailSubscription =
        emailSubscriptionsList.find(
          (subscription) =>
            subscription.type === notification.type &&
            subscription.role === "ADMIN",
        )?.uuid ?? null;

      const userEmailSubscription =
        emailSubscriptionsList.find(
          (subscription) =>
            subscription.type === notification.type &&
            subscription.role === "USER",
        )?.uuid ?? null;

      const platformSubscription =
        platformSubscriptionsList.find(
          (subscription) => subscription.type === notification.type,
        )?.uuid ?? null;

      return {
        ...notification,
        platformNotificationUuid: platformSubscription,
        ownerEmailNotificationUuid: ownerEmailSubscription,
        adminEmailNotificationUuid: adminEmailSubscription,
        userEmailNotificationUuid: userEmailSubscription,
      };
    });
  }, [emailSubscriptionsList, platformSubscriptionsList]);

  useEffect(() => {
    if (company && getEmailSubscriptionsListStatus === "idle") {
      void dispatch(getEmailSubscriptionsListAsync(company.uuid));
    }
  }, [company, dispatch, getEmailSubscriptionsListStatus]);

  useEffect(() => {
    if (company && getPlatformSubscriptionsStatus === "idle") {
      void dispatch(getPlatformSubscriptionsAsync(company.uuid));
    }
  }, [company, dispatch, getPlatformSubscriptionsStatus]);

  if (params.tab !== "management") {
    return;
  }

  return notificationsCategories.map((category) => {
    const notificationsOfCurrentCategory = allSubscriptionsDisplayed.filter(
      (notification) => notification.category === category,
    );
    return (
      <div key={category}>
        <StyledFlex>
          <StyledHeaders>
            <StyledAllWidthColumn>
              <StyledCategory>
                <TextCapitalized>
                  {t(`notifications.category.${category}`)}
                </TextCapitalized>
              </StyledCategory>
            </StyledAllWidthColumn>
            <StyledNotificationTypeColumn>
              <StyledTypeHeader>
                <TextCapitalized>
                  {t(`notifications.type.PLATEFORM`)}
                </TextCapitalized>
              </StyledTypeHeader>
            </StyledNotificationTypeColumn>
            <Spacer x={1} />
            <StyledNotificationTypeColumn>
              <StyledTypeHeader>
                <TextCapitalized>
                  {t(`notifications.type.OWNER_EMAIL`)}
                </TextCapitalized>
              </StyledTypeHeader>
            </StyledNotificationTypeColumn>
            <Spacer x={1} />
            <StyledNotificationTypeColumn>
              <StyledTypeHeader>
                <TextCapitalized>
                  {t(`notifications.type.ADMIN_EMAIL`)}
                </TextCapitalized>
              </StyledTypeHeader>
            </StyledNotificationTypeColumn>
            <Spacer x={1} />
            <StyledNotificationTypeColumn>
              <StyledTypeHeader>
                <TextCapitalized>
                  {t(`notifications.type.USER_EMAIL`)}
                </TextCapitalized>
              </StyledTypeHeader>
            </StyledNotificationTypeColumn>
            <Spacer x={1} />
          </StyledHeaders>
        </StyledFlex>
        <Spacer y={1} />
        <StyledNotifications>
          <Spacer x={1} />
          <StyledColumn>
            <Spacer y={0.5} />
            {notificationsOfCurrentCategory.map((notif) => {
              return (
                <StyledNotificationRow key={String(notif.type)}>
                  <Spacer y={1} />
                  <StyledFlex>
                    <StyledAllWidthColumn>
                      <TextCapitalized>
                        {t(`notifications.title.${String(notif.type)}`)}
                      </TextCapitalized>
                      <ParametersSelect
                        notif={notif}
                        parameters={parameters}
                        onChange={(val) =>
                          setParameters((prev) => ({
                            ...prev,
                            [String(notif.type)]:
                              prev[String(notif.type)] && val
                                ? {
                                    ...prev[String(notif.type)],
                                    ...val,
                                  }
                                : (val ?? null),
                          }))
                        }
                      />
                    </StyledAllWidthColumn>
                    <StyledNotificationTypeColumn>
                      <TextCapitalized>
                        {notif.isPlatformNotificationVisible ? (
                          <Switch
                            onClick={() => {
                              notif.platformNotificationUuid
                                ? handleDeletePlatformSubscription(
                                    notif.platformNotificationUuid,
                                  )
                                : handleAddPlatformSubscription(notif);
                              dispatch(
                                notificationsSlice.actions.resetPlatformSubscriptionsListStatus(),
                              );
                            }}
                            checked={!!notif.platformNotificationUuid}
                            size={"S"}
                          />
                        ) : (
                          ""
                        )}
                      </TextCapitalized>
                    </StyledNotificationTypeColumn>
                    <Spacer x={1} />
                    <StyledNotificationTypeColumn>
                      <TextCapitalized>
                        {notif.isOwnerEmailNotificationVisible ? (
                          <Switch
                            onClick={() => {
                              notif.ownerEmailNotificationUuid
                                ? handleDeleteEmailSubscription(
                                    notif.ownerEmailNotificationUuid,
                                  )
                                : handleAddEmailSubscription(
                                    notif.type,
                                    "OWNER",
                                    notif,
                                  );
                              dispatch(
                                notificationsSlice.actions.resetEmailSubscriptionsListStatus(),
                              );
                            }}
                            checked={!!notif.ownerEmailNotificationUuid}
                            size={"S"}
                          />
                        ) : (
                          ""
                        )}
                      </TextCapitalized>
                    </StyledNotificationTypeColumn>
                    <Spacer x={1} />
                    <StyledNotificationTypeColumn>
                      <TextCapitalized>
                        {notif.isAdminEmailNotificationVisible ? (
                          <Switch
                            onClick={() => {
                              notif.adminEmailNotificationUuid
                                ? handleDeleteEmailSubscription(
                                    notif.adminEmailNotificationUuid,
                                  )
                                : handleAddEmailSubscription(
                                    notif.type,
                                    "ADMIN",
                                    notif,
                                  );
                              dispatch(
                                notificationsSlice.actions.resetEmailSubscriptionsListStatus(),
                              );
                            }}
                            checked={!!notif.adminEmailNotificationUuid}
                            size={"S"}
                          />
                        ) : (
                          ""
                        )}
                      </TextCapitalized>
                    </StyledNotificationTypeColumn>
                    <Spacer x={1} />
                    <StyledNotificationTypeColumn>
                      <TextCapitalized>
                        {notif.isUserEmailNotificationVisible ? (
                          <Switch
                            onClick={() => {
                              notif.userEmailNotificationUuid
                                ? handleDeleteEmailSubscription(
                                    notif.userEmailNotificationUuid,
                                  )
                                : handleAddEmailSubscription(
                                    notif.type,
                                    "USER",
                                    notif,
                                  );
                              dispatch(
                                notificationsSlice.actions.resetEmailSubscriptionsListStatus(),
                              );
                            }}
                            checked={!!notif.userEmailNotificationUuid}
                            size={"S"}
                          />
                        ) : (
                          ""
                        )}
                      </TextCapitalized>
                    </StyledNotificationTypeColumn>
                  </StyledFlex>
                  <Spacer y={1} />
                </StyledNotificationRow>
              );
            })}
            <Spacer y={0.5} />
          </StyledColumn>
          <Spacer x={1} />
        </StyledNotifications>
        <Spacer y={2} />
      </div>
    );
  });
};

const StyledCategory = styled.div`
  display: flex;
  ${typographies["Header/H2"]}
  width: 100%;
`;

const StyledHeaders = styled.div`
  display: flex;
  width: 100%;
  min-width: 35rem;
`;

const StyledNotifications = styled.div`
  display: flex;
  width: 100%;
  border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
  border-radius: 0.5rem;
  min-width: 35rem;
`;

export const StyledNotificationTypeColumn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 15%;
  max-width: 15%;
`;
const StyledAllWidthColumn = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const StyledNotificationRow = styled.div`
  ${typographies["Body/S"]};
  width: 100%;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledTypeHeader = styled.div`
  display: flex;
  ${typographies["Header/H5"]};
`;
